import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';

const ParentActivatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resending, setResending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (!token) {
      setError('Activation token is missing.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await clubsBookingApi.parentActivate(token, password);
      auth.setAuth(response.accessToken, response.id, response.name, response.email || undefined, 'parent');
      clubsBookingApi.setAuthToken(response.accessToken);
      navigate('/my-bookings');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not activate account');
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (!email.trim()) {
      setError('Enter your email to resend a setup link.');
      return;
    }

    setResending(true);
    setError('');
    setNotice('');
    try {
      const response = await clubsBookingApi.resendParentActivationLink(email.trim());
      setNotice(response?.message || 'If your account needs activation, a fresh link has been sent.');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not resend setup link');
    } finally {
      setResending(false);
    }
  };

  const canResend = !token || /expired|invalid|incomplete/i.test(error);

  return (
    <main className="min-h-screen bg-[#f1f2f7] px-4 py-10">
      <section className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <img src="/logo.png" alt="Session Monkey" className="h-8 w-8 rounded-md object-contain" />
          <p className="text-lg font-semibold text-slate-900">Session Monkey</p>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Set your password</h1>
        <p className="mt-2 text-sm text-slate-600">Create a password to activate your parent account and manage bookings.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-sm text-slate-700">Password</span>
            <input type="password" minLength={8} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <label className="block">
            <span className="text-sm text-slate-700">Confirm password</span>
            <input type="password" minLength={8} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {notice && <p className="text-sm text-emerald-700">{notice}</p>}
          <button type="submit" disabled={loading} className="w-full rounded-lg bg-[#1F8F5F] px-4 py-2 text-white hover:bg-[#1F8F5F] disabled:opacity-70">
            {loading ? 'Saving...' : 'Activate account'}
          </button>
        </form>

        {canResend && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-sm text-emerald-800">Need a fresh setup link?</p>
            <label className="mt-2 block">
              <span className="text-sm text-slate-700">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <button
              type="button"
              onClick={() => void onResend()}
              disabled={resending}
              className="mt-3 rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-70"
            >
              {resending ? 'Resending...' : 'Resend setup link'}
            </button>
          </div>
        )}

        <Link to="/parent/login" className="mt-4 inline-block text-sm text-slate-700 underline">Back to parent login</Link>
      </section>
    </main>
  );
};

export default ParentActivatePage;


